import { useCallback, useEffect, useRef } from 'react';

import { useNotifications } from '@gasbi/shared/common/providers/NotificationProvider/hooks/useNotifications';
import { useActStepperEngine } from '@gasbi/shared/web/components/ActStepperEngine';
import { useParams } from 'react-router-dom';

import { ViolationActRequestBody } from '@/features/violationActs/types';
import { ViolationActFormStep } from '@/pages/ViolationActForm';
import { getErrorMessages } from '@/pages/ViolationActForm/hooks/submitFormUtils/getErrorMessages';
import { prepareRequestBody } from '@/pages/ViolationActForm/hooks/submitFormUtils/prepareRequestBody';

type ValidateServerStepType = (args: {
  isMultipartFormData?: boolean;
  step?: ViolationActFormStep;
}) => Promise<boolean>;

type ValidationMutationHook = (
  options?: any,
) => readonly [(body: any) => { unwrap: () => Promise<unknown> }, ...any[]];

const getRequestBodyByStep = (
  requestBody: ViolationActRequestBody,
  step: ViolationActFormStep,
) => {
  if (step === ViolationActFormStep.Information) {
    return {
      ...requestBody,
      modems: [],
      devices: [],
    };
  }

  return requestBody;
};

export const useServerStepValidation = (
  validateCreateQuery: ValidationMutationHook,
  validateUpdateQuery: ValidationMutationHook,
) => {
  const { push } = useNotifications();

  const { meteringPointId, commercialMeteringPointId } = useParams<{
    meteringPointId?: string;
    commercialMeteringPointId?: string;
  }>();

  const stepsState = useActStepperEngine(s => s.stepsState);
  const isEditMode = useActStepperEngine(s => s.isEditMode);

  const stepsStateRef = useRef(stepsState);
  useEffect(() => {
    stepsStateRef.current = stepsState;
  }, [stepsState]);

  const validationQuery = isEditMode
    ? validateUpdateQuery
    : validateCreateQuery;

  const [validate] = validationQuery();

  const validateServerStep = useCallback<ValidateServerStepType>(
    async ({ step }) => {
      let preparedRequestBody = prepareRequestBody({
        stepsState: stepsStateRef.current,
        meteringPointId,
        commercialMeteringPointId,
        steps: ViolationActFormStep,
        isEditMode,
      });

      preparedRequestBody = getRequestBodyByStep(preparedRequestBody, step);

      const formData = new FormData();
      formData.append('id', preparedRequestBody.id);
      formData.append('json', JSON.stringify(preparedRequestBody));

      try {
        await validate(formData).unwrap();

        return true;
      } catch (e) {
        const errorMessages = getErrorMessages(e);

        push({
          message: Array.isArray(errorMessages)
            ? errorMessages[0]
            : errorMessages,
          type: 'danger',
        });
        return false;
      }
    },
    [meteringPointId, commercialMeteringPointId, isEditMode, validate, push],
  );

  return { validateServerStep };
};
